import React, {useEffect, useState} from 'react';
import {Button, Modal} from 'react-bootstrap';
import {ErrorToast, SuccessToast} from '../../../utils/toasters';
import {CompanyBrandAclResponse} from '../../../model/company/Company';
import {CompanyService} from '../../company/CompanyService';


interface CompanyRolesAssignModalProps {
    show: boolean;
    closeModal: () => void;
    companyId?: number;
    companyName?: string;
}

function CompanyRolesAssignModal({show, closeModal, companyId, companyName}: CompanyRolesAssignModalProps) {

    const [roles, setRoles] = useState<CompanyBrandAclResponse[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show && companyId) {
            setLoading(true);
            CompanyService.getCompanyBrandAcls(companyId)
                .then(data => {
                    setRoles(data);
                    setSelectedIds(new Set(data.filter(r => r.isSelected).map(r => r.id)));
                })
                .catch(err => ErrorToast(err))
                .finally(() => setLoading(false));
        }
    }, [show, companyId]);

    const toggleRole = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const saveRoles = () => {
        if (!companyId) return;

        setSaving(true);
        CompanyService.updateCompanyBrandAcls(companyId, { aclIds: Array.from(selectedIds) })
            .then(() => {
                SuccessToast('Roles updated successfully');
                closeModal();
            })
            .catch(err => ErrorToast(err))
            .finally(() => setSaving(false));
    };

    return (
        <Modal show={show} onHide={closeModal} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <div>
                    <Modal.Title>Add possible roles - {companyName}</Modal.Title>
                    <small className="text-muted">Select the roles to add.</small>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="fw-bold text-uppercase small">Possible roles</span>
                    <small className="text-muted">{selectedIds.size} selected</small>
                </div>

                {loading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : (
                    <div className="row g-2">
                        {roles.map(role => {
                            const isChecked = selectedIds.has(role.id);
                            return (
                                <div key={role.id} className="col-3">
                                    <div
                                        role="button"
                                        onClick={() => toggleRole(role.id)}
                                        className={`border rounded p-3 h-100 d-flex gap-2 ${isChecked ? 'border-primary bg-light' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="form-check-input mt-1"
                                            checked={isChecked}
                                            onChange={() => toggleRole(role.id)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        <div>
                                            <div className="fw-semibold">{role.name}</div>
                                            <div className="small text-muted">{role.key ?? '-'}</div>
                                            <small className="text-muted">{role.acl?.length ?? 0} permissions</small>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex align-items-center justify-content-between">
                <small className="text-muted">{selectedIds.size} selected</small>
                <div>
                    <Button variant="outline-primary" onClick={closeModal} className="me-2">
                        Close
                    </Button>
                    <Button onClick={saveRoles} disabled={saving}>
                        {saving ? 'Saving...' : 'Add selected'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default CompanyRolesAssignModal;